import { type WorkflowStageValue } from "@/lib/constants/testing-constants";

interface WorkflowStage {
  number: number;
  name: string;
  dbValue: WorkflowStageValue;
  gate?: string | null;
  description?: string;
}

interface WorkflowProgressBarProps {
  stages: readonly WorkflowStage[];
  currentStageNumber: number;
  currentStageComplete: boolean;
  className?: string;
}

// Abbreviated stage names for compact display
const STAGE_ABBREVIATIONS: Record<string, string> = {
  "Sprint Analysis": "Analysis",
  "Architecture Planning": "Architecture",
  "User Approval": "Approval",
  "Database Migration": "Database",
  "List Page Implementation": "List Page",
  "Detail Page Implementation": "Detail Page",
  "Forms Implementation": "Forms",
  "Visual Testing": "Visual Test",
  "Test Execution": "Test Exec",
  "Results Verification": "Results",
  "Pass Rate Validation": "Pass Rate",
  "Manual Testing": "Manual Test",
  "Issue Resolution": "Issues",
  "Sprint Complete": "Complete",
};

export function WorkflowProgressBar({
  stages,
  currentStageNumber,
  currentStageComplete,
  className = "",
}: WorkflowProgressBarProps) {
  // If current stage is complete, the next stage is current
  // Otherwise, the current stage itself is current
  const activeStageNumber = currentStageComplete ? currentStageNumber + 1 : currentStageNumber;

  return (
    <div className={`w-full ${className}`}>
      {/* Progress bar container */}
      <div className="relative flex items-center justify-between">
        {/* Background line - positioned at vertical center of squares (h-6 = 24px, so top-3 = 12px centers it) */}
        <div className="absolute top-3 left-0 right-0 h-0.5 bg-gray-200 dark:bg-gray-700" aria-hidden="true" />

        {/* Stage indicators */}
        <div className="relative flex w-full justify-between">
          {stages.map((stage) => {
            // Stage is complete if it's before the active stage
            const isComplete = stage.number < activeStageNumber;
            // Stage is current if it matches the active stage
            const isCurrent = stage.number === activeStageNumber;

            const abbreviatedName = STAGE_ABBREVIATIONS[stage.name] || stage.name;

            return (
              <div
                key={stage.number}
                className="group relative flex flex-col items-center"
                title={`Stage ${stage.number}: ${stage.name}${stage.gate ? ` (${stage.gate})` : ""}`}
              >
                {/* Square indicator */}
                <div
                  className={`relative flex h-6 w-6 items-center justify-center rounded-md border-2 transition-all ${
                    isComplete
                      ? "border-green-300 bg-green-300 text-white"
                      : isCurrent
                      ? "border-blue-600 bg-blue-600 text-white ring-4 ring-blue-600/20 ring-offset-2"
                      : "border-gray-300 bg-white text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400"
                  }`}
                >
                  <span className="text-xs font-semibold">{stage.number}</span>
                </div>

                {/* Stage name - always visible */}
                <div className="mt-1 text-[10px] text-center text-gray-600 dark:text-gray-400 max-w-[60px] leading-tight">
                  {abbreviatedName}
                </div>

                {/* Detailed tooltip - shown on hover */}
                <div className="absolute top-8 hidden min-w-[120px] group-hover:block z-10">
                  <div className="rounded-md bg-gray-900 px-2 py-1 text-center text-xs text-white shadow-lg dark:bg-gray-700">
                    <div className="font-semibold">{stage.name}</div>
                    {stage.gate && (
                      <div className="text-[10px] text-gray-300 mt-0.5">{stage.gate}</div>
                    )}
                    {/* Arrow pointing up */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-2 w-2 rotate-45 bg-gray-900 dark:bg-gray-700" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend below progress bar */}
      <div className="mt-6 flex items-center justify-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-md border-2 border-green-300 bg-green-300" />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-md border-2 border-blue-600 bg-blue-600 ring-2 ring-blue-600/20" />
          <span>Current</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="h-3 w-3 rounded-md border-2 border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800" />
          <span>Pending</span>
        </div>
      </div>
    </div>
  );
}
